import {
  ApiRequest,
  ApiResponse,
  fetchDataFromAPITyped,
} from './api-requests';
import {
  PairingCancelResponse,
  PairingStartResponse,
  RfidCardResponse,
  RfidCardsListResponse,
  RfidTapsResponse,
} from '../models/rfid-card-model';

/**
 * Client wrapper for the RFID tap-to-charge endpoints documented in
 * `meander-backend/docs/RFID_TAP_TO_CHARGE_FRONTEND_CONTRACT.md`.
 *
 * All endpoints are JWT-authenticated; the shared `fetchDataFromAPITyped`
 * helper attaches the `Authorization` header from AsyncStorage.
 */

export const listRfidCards = async (): Promise<
  ApiResponse<RfidCardsListResponse | null>
> => {
  const apiQuery: ApiRequest = {
    route: 'rfid/cards',
    request: { method: 'GET', entity: 'rfid', body: undefined },
  };
  return fetchDataFromAPITyped<RfidCardsListResponse>(apiQuery);
};

export const updateRfidCard = async (
  cardUuid: string,
  body: { nickname?: string | null; auto_charge_enabled?: boolean },
): Promise<ApiResponse<RfidCardResponse | null>> => {
  const apiQuery: ApiRequest = {
    route: `rfid/cards/${cardUuid}`,
    request: { method: 'PATCH', entity: 'rfid', body },
  };
  return fetchDataFromAPITyped<RfidCardResponse>(apiQuery);
};

export const disableRfidCard = async (
  cardUuid: string,
  reason?: string,
): Promise<ApiResponse<RfidCardResponse | null>> => {
  const apiQuery: ApiRequest = {
    route: `rfid/cards/${cardUuid}/disable`,
    request: {
      method: 'POST',
      entity: 'rfid',
      body: reason ? { reason } : {},
    },
  };
  return fetchDataFromAPITyped<RfidCardResponse>(apiQuery);
};

export const enableRfidCard = async (
  cardUuid: string,
): Promise<ApiResponse<RfidCardResponse | null>> => {
  const apiQuery: ApiRequest = {
    route: `rfid/cards/${cardUuid}/enable`,
    request: { method: 'POST', entity: 'rfid', body: {} },
  };
  return fetchDataFromAPITyped<RfidCardResponse>(apiQuery);
};

export const revokeRfidCard = async (
  cardUuid: string,
  reason?: string,
): Promise<ApiResponse<RfidCardResponse | null>> => {
  const apiQuery: ApiRequest = {
    route: `rfid/cards/${cardUuid}/revoke`,
    request: {
      method: 'POST',
      entity: 'rfid',
      body: reason ? { reason } : {},
    },
  };
  return fetchDataFromAPITyped<RfidCardResponse>(apiQuery);
};

export const getRfidCardTaps = async (
  cardUuid: string,
  limit = 20,
): Promise<ApiResponse<RfidTapsResponse | null>> => {
  const apiQuery: ApiRequest = {
    route: `rfid/cards/${cardUuid}/taps`,
    request: {
      method: 'GET',
      entity: 'rfid',
      body: undefined,
      searchParams: new URLSearchParams({ limit: String(limit) }),
    },
  };
  return fetchDataFromAPITyped<RfidTapsResponse>(apiQuery);
};

export const startRfidPairing = async (body?: {
  charger_uuid?: string;
  nickname?: string;
}): Promise<ApiResponse<PairingStartResponse | null>> => {
  const apiQuery: ApiRequest = {
    route: 'rfid/pairing/start',
    request: { method: 'POST', entity: 'rfid', body: body ?? {} },
  };
  return fetchDataFromAPITyped<PairingStartResponse>(apiQuery);
};

export const cancelRfidPairing = async (
  claimToken: string,
): Promise<ApiResponse<PairingCancelResponse | null>> => {
  const apiQuery: ApiRequest = {
    route: 'rfid/pairing/cancel',
    request: {
      method: 'POST',
      entity: 'rfid',
      body: { claim_token: claimToken },
    },
  };
  return fetchDataFromAPITyped<PairingCancelResponse>(apiQuery);
};
